import { standaloneSvg } from '@civil/annotate';
import type { UiSnapshot } from '@civil/schema';
import { apiFetch } from './project.js';

/**
 * A snapshot with its marks drawn on (docs/ui-tab.md §7, §8), as an agent should see
 * it: for the interpretation, and for an export's images. Drawn in the browser, with
 * the same marks the tab shows (packages/annotate); the server keeps only the
 * screenshot and the marks, never a picture of both.
 */

const MAX_BYTES = 5 * 1024 * 1024;

async function loadImage(src: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.decoding = 'async';
  const loaded = new Promise<void>((ok, fail) => {
    image.onload = () => ok();
    image.onerror = () => fail(new Error('the image could not be loaded'));
  });
  image.src = src;
  await loaded;
  return image;
}

/** The snapshot with its marks drawn on, as PNG — or JPEG when a PNG would pass the 5 MB a model reads. */
export async function drawnSnapshot(snapshot: UiSnapshot): Promise<Blob> {
  const response = await apiFetch(snapshot.image);
  if (!response.ok) throw new Error(`snapshot ${snapshot.position + 1}'s image is no longer kept`);
  const screenshotUrl = URL.createObjectURL(await response.blob());
  const marks = snapshot.marks.map((m, i) => ({ id: m.id, number: i + 1, geometry: m.geometry }));
  const overlayUrl = URL.createObjectURL(
    new Blob([standaloneSvg(marks, { width: snapshot.width, height: snapshot.height })], { type: 'image/svg+xml' }),
  );
  try {
    const [screenshot, overlay] = await Promise.all([loadImage(screenshotUrl), loadImage(overlayUrl)]);
    const canvas = document.createElement('canvas');
    canvas.width = snapshot.width;
    canvas.height = snapshot.height;
    const g = canvas.getContext('2d');
    if (!g) throw new Error('this browser cannot draw the marks');
    g.drawImage(screenshot, 0, 0, snapshot.width, snapshot.height);
    g.drawImage(overlay, 0, 0, snapshot.width, snapshot.height);
    const png = await toBlob(canvas, 'image/png');
    return png.size <= MAX_BYTES ? png : await toBlob(canvas, 'image/jpeg', 0.85);
  } finally {
    URL.revokeObjectURL(screenshotUrl);
    URL.revokeObjectURL(overlayUrl);
  }
}

const toBlob = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> =>
  new Promise((ok, fail) => canvas.toBlob((b) => (b ? ok(b) : fail(new Error('the drawing could not be made'))), type, quality));

export const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((ok, fail) => {
    const reader = new FileReader();
    reader.onload = () => ok(String(reader.result));
    reader.onerror = () => fail(reader.error ?? new Error('could not read the drawing'));
    reader.readAsDataURL(blob);
  });
